import Loader from "@/components/Loader";
import api from "@/http/api";
import { useAuthStore } from "@/store";
import type { NotificationType } from "@/types";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query"
import RenderNotification, { type NotificationFilters, type PaginationInfo, type OrderRefOption } from '@univdiam/ui/customComponents/Notifications'
import { useEffect, useState, useCallback } from "react";
const Notifications = () => {
    const queryClient = useQueryClient()
    const { setNotificationCount } = useAuthStore()

    const [filters, setFilters] = useState<NotificationFilters>({
        order_id: '',
        action_type: '',
        search: '',
        start_date: '',
        end_date: '',
        is_seen: 'unread',
        page: 1
    })

    const [pagination, setPagination] = useState<PaginationInfo | null>(null)

    // Build query string from filters
    const buildQueryString = useCallback(() => {
        const params = new URLSearchParams()
        if (filters.order_id) params.set('order_id', filters.order_id)
        if (filters.action_type) params.set('action_type', filters.action_type)
        if (filters.search) params.set('search', filters.search)
        if (filters.start_date) params.set('start_date', filters.start_date)
        if (filters.end_date) params.set('end_date', filters.end_date)
        if (filters.is_seen) params.set('is_seen', filters.is_seen)
        params.set('page', String(filters.page))
        params.set('limit', '50')
        return params.toString()
    }, [filters])

    const { data, isPending, isFetching } = useQuery({
        queryKey: ["notifications", filters],
        queryFn: async () => {
            const qs = buildQueryString()
            const res = await api.get(`/notification?${qs}`)
            return res.data
        },
    });

    // Fetch order options for filter dropdown
    const { 
        data: orderRefsData,
        fetchNextPage: fetchNextOrderRefs,
        hasNextPage: hasNextOrderRefs,
        isFetchingNextPage: isFetchingNextOrderRefs
    } = useInfiniteQuery({
        queryKey: ["notification-order-refs"],
        queryFn: async ({ pageParam = 1 }) => {
            const res = await api.get(`/notification/order-refs?page=${pageParam}&limit=20`)
            return res.data
        },
        getNextPageParam: (lastPage) => {
            if (!lastPage.pagination) return undefined;
            return lastPage.pagination.page < lastPage.pagination.totalPages 
                ? lastPage.pagination.page + 1 
                : undefined;
        },
        initialPageParam: 1,
        staleTime: 60000,
    });

    const orderOptions: OrderRefOption[] = orderRefsData?.pages.flatMap(p => p.orders) || []

    // Update pagination when data changes
    useEffect(() => {
        if (data?.pagination) {
            setPagination(data.pagination)
        }
    }, [data?.pagination])

    // Re-sync notification count from server when visiting this page
    useEffect(() => {
        api.get('/notification/unseen-count').then(res => {
            if (res.data.unseenCount !== undefined) {
                setNotificationCount(res.data.unseenCount)
            }
        }).catch(console.error)
    }, [])

    const handleMarkAsSeen = async (id: string | number) => {
        // Optimistically update immediately so the badge drops before navigation
        setNotificationCount(Math.max(0, useAuthStore.getState().notificationCount - 1))
        queryClient.setQueryData(["notifications", filters], (oldData: any) => {
            if (!oldData?.notifications) return oldData
            return {
                ...oldData,
                notifications: oldData.notifications.map((n: NotificationType) =>
                    n.id === id ? { ...n, is_seen: 1 } : n
                )
            }
        })

        try {
            const res = await api.post(`/notification/${id}/mark-seen`)
            // Sync with server's authoritative count
            if (res.data.unseenCount !== undefined) {
                setNotificationCount(res.data.unseenCount)
            }
        } catch (error) {
            console.error("Failed to mark notification as seen", error)
        }
    }

    const handleFilterChange = useCallback((partial: Partial<NotificationFilters>) => {
        setFilters(prev => ({ ...prev, ...partial }))
    }, [])

    const handlePageChange = useCallback((page: number) => {
        setFilters(prev => ({ ...prev, page }))
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }, [])

    if (isPending && !isFetching) {
        return <Loader />
    }

    const notifications: NotificationType[] = data?.notifications || []


    return (
        <div>
            <RenderNotification
                data={notifications}
                onMarkAsSeen={handleMarkAsSeen}
                filters={filters}
                onFilterChange={handleFilterChange}
                orderOptions={orderOptions}
                fetchNextOrderRefs={fetchNextOrderRefs}
                hasNextOrderRefs={hasNextOrderRefs}
                isFetchingNextOrderRefs={isFetchingNextOrderRefs}
                pagination={pagination || undefined}
                onPageChange={handlePageChange}
            />
        </div>
    )
}

export default Notifications